import { useSupportedJurisdictions } from '@commenda/api';
import { Icons } from '@commenda/kakashi';
import { IBusinessEntity } from '@commenda/types';
import { IServiceRequest } from '@commenda/types/src/service-request/types';
import { TWorkRequest } from '@commenda/types/src/work-request/types';
import { CorporationModel } from '@commenda/utils';

// `workRequests` / `serviceRequests` are accepted but currently unused — the
// EntitiesGrid call site forwards them so a future change can light up
// per-entity badges (e.g. "incorporation pending review") without re-shaping
// the call signature again.
export const useEntitiesGridView = (
  businessEntitiesResponse: IBusinessEntity[],
  _workRequests?: TWorkRequest[],
  _serviceRequests?: IServiceRequest[]
) => {
  const { data: supportedJurisdictions } = useSupportedJurisdictions();

  const businessEntities = businessEntitiesResponse
    .map((businessEntity) => ({
      id: businessEntity.id,
      incorporationCountry:
        businessEntity.generalCorporationInfo.incorporationCountry,
      name: businessEntity.generalCorporationInfo.legalName,
      corporationType: businessEntity.generalCorporationInfo.businessEntityType,
      tooltip: businessEntity.featureSpecificInfo?.incorporation
        ?.isIncorporationInProgress
        ? {
            icon: (
              <Icons.Hourglass01 className="size-4 text-fg-warning-hover" />
            ),
            text: 'Incorporation in progress',
          }
        : null,
      link: `/corporation/${businessEntity.id}`,
      isIncorporationInProgress:
        businessEntity.featureSpecificInfo?.incorporation
          ?.isIncorporationInProgress ?? false,
    }))
    .sort((a, b) => {
      // Active entities first, incorporation in progress last
      if (a.isIncorporationInProgress && !b.isIncorporationInProgress) return 1;
      if (!a.isIncorporationInProgress && b.isIncorporationInProgress)
        return -1;

      // Within each group: entity management supported before unsupported
      const aIsSupported = CorporationModel.isSupportedEntityManagement(
        a.incorporationCountry,
        supportedJurisdictions?.jurisdictions
      );
      const bIsSupported = CorporationModel.isSupportedEntityManagement(
        b.incorporationCountry,
        supportedJurisdictions?.jurisdictions
      );
      if (aIsSupported && !bIsSupported) return -1;
      if (!aIsSupported && bIsSupported) return 1;

      return 0;
    });

  const numberOfIncorporationInProgress = businessEntities.filter(
    (e) => e.isIncorporationInProgress
  ).length;

  return { businessEntities, numberOfIncorporationInProgress };
};
